import styles from './SelectDropdown.module.scss'
import React, { ComponentType, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Col, Container, Row, Spinner } from 'react-bootstrap'
import classNames from 'classnames'
import styled from 'styled-components'
import { useOutsideClick } from '../../common/useOutsideClick'
import { useDebounce } from 'use-debounce'
import { Button } from '../Button/Button'

export interface SelectOption {
  id: string
  label: string
  displayLabel?: React.ReactNode
  disabled?: boolean
  className?: string
  tooltipLabel?: string
}

export interface DropDownItemOptionComponentProps {
  optionId: string
  optionLabel: string
  displayLabel?: React.ReactNode
  tooltipLabel?: string
  onClick: React.MouseEventHandler<HTMLElement>
  disabled?: boolean
  selected?: boolean
  deselect?: boolean
  className?: string
}

export interface DropdownIconComponentProps {
  selectedOption: SelectOption | null
}

export type SelectDropdownRef = {
  focus: () => void
}

export interface SelectDropdownProps {
  options: SelectOption[]
  initialSelectedOption?: SelectOption | null
  placeholder?: string
  className?: string
  onOptionSelected?: (option: SelectOption) => void
  onSearchInputSelected?: (searchInput: string) => void
  disabled?: boolean
  inactive?: boolean
  searchable?: boolean
  onSearchInputChange?: (inputString: string) => Promise<void>
  DropDownItemOptionComponent?: ComponentType<DropDownItemOptionComponentProps>
  label?: string
  icon?: string
  iconClassName?: string
  iconOnly?: boolean
  IconComponent?: ComponentType<DropdownIconComponentProps>
  hideArrows?: boolean
  scrollWhenOpened?: boolean
  DropdownHeaderComponent?: ComponentType
  DropdownFooterComponent?: ComponentType
  loading?: boolean
  showDeselectOption?: boolean
  deselectOptionLabel?: string
  onDeselect?: () => void
  clearDropdownOnOptionSelected?: boolean
  showSearchInputSelectButton?: boolean
  forceDefaultDeselectOptionComponent?: boolean
  hideClearButton?: boolean
  searchInputClassName?: string
  inputName?: string
  inputAutoComplete?: string
}

export const CustomDropdownItemOption = styled.div`
  padding: 0.25rem 0.5rem;

  &:hover {
    border-radius: 0.25rem;
    background: var(--zocks-primary-light, #ebe5fc);
  }
`

export const DefaultDropdownItemOption: React.FC<DropDownItemOptionComponentProps> = ({
  optionLabel,
  onClick,
  disabled = false,
  deselect = false,
  className = '',
}) => {
  return (
    <CustomDropdownItemOption
      role='button'
      onClick={onClick}
      className={classNames(className, {
        [styles.selectDropdownCustomItemOptionDisabled]: disabled,
        [styles.selectDropdownCustomItemOptionIsDeselect]: deselect,
      })}
    >
      {optionLabel}
    </CustomDropdownItemOption>
  )
}

export const SelectDropdown = forwardRef<SelectDropdownRef, SelectDropdownProps>(
  (
    {
      options,
      initialSelectedOption = options[0],
      placeholder = 'Select Options',
      className,
      onOptionSelected,
      onSearchInputSelected,
      disabled = false,
      inactive = false,
      searchable = false,
      onSearchInputChange,
      DropDownItemOptionComponent,
      icon,
      iconClassName = '',
      iconOnly = false,
      IconComponent,
      label,
      hideArrows = false,
      scrollWhenOpened = false,
      DropdownHeaderComponent,
      DropdownFooterComponent,
      loading = false,
      showDeselectOption = false,
      deselectOptionLabel = 'Select Options',
      onDeselect,
      clearDropdownOnOptionSelected = false,
      showSearchInputSelectButton = false,
      forceDefaultDeselectOptionComponent = false,
      hideClearButton = false,
      searchInputClassName = '',
      inputName,
      inputAutoComplete,
    },
    ref,
  ) => {
    const dropdownDivRef = useRef<HTMLDivElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)

    const [selectedOption, setSelectedOption] = useState<SelectOption | null>(initialSelectedOption)
    const [isOpen, setIsOpen] = useState(false)
    const [filteredOptions, setFilteredOptions] = useState<SelectOption[]>(options)
    const [searchInput, setSearchInput] = useState<string>('')
    const [debouncedSearchInput] = useDebounce(searchInput, 400)
    const [loadingSearchInput, setLoadingSearchInput] = useState<boolean>(false)

    useEffect(() => {
      if (selectedOption?.label === debouncedSearchInput) {
        return
      }
      if (onSearchInputChange) {
        setLoadingSearchInput(true)
        onSearchInputChange(debouncedSearchInput).finally(() => {
          setLoadingSearchInput(false)
        })
      }
    }, [debouncedSearchInput])

    useEffect(() => {
      setSelectedOption(initialSelectedOption)
      setSearchInput(initialSelectedOption?.label ?? '')
    }, [initialSelectedOption])

    useEffect(() => {
      if (isOpen && scrollWhenOpened) {
        dropdownDivRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    }, [isOpen, scrollWhenOpened])

    useImperativeHandle(ref, () => {
      return {
        focus() {
          setIsOpen(true)
          if (!searchInputRef.current) {
            return
          }
          searchInputRef.current.focus()
        },
      }
    }, [])

    const toggleDropdown = () => {
      if (!disabled && !inactive) {
        setIsOpen(!isOpen)
      }
    }

    const handleOnOptionSelected = (option: SelectOption) => {
      if (clearDropdownOnOptionSelected) {
        setSearchInput('')
        setSelectedOption(null)
      } else {
        setSearchInput(option.label)
        setSelectedOption(option)
      }
      setFilteredOptions(options)
      setIsOpen(false)
      onOptionSelected?.(option)
    }

    const handleOnDeselect = () => {
      setSearchInput('')
      setFilteredOptions(options)
      setSelectedOption(null)
      setIsOpen(false)
      onDeselect?.()
    }

    const containerRef = useRef<HTMLDivElement>(null)

    useOutsideClick(containerRef, () => {
      setIsOpen(false)
    })

    const DropDownItemOption = DropDownItemOptionComponent ?? DefaultDropdownItemOption

    const DeselectDropDownItemOption = forceDefaultDeselectOptionComponent ? DefaultDropdownItemOption : DropDownItemOption

    const isValidUserInput = () => {
      return searchInput.trim().length > 0
    }

    const handleSearchInputSelected = () => {
      if (searchInput && isValidUserInput() && onSearchInputSelected) {
        onSearchInputSelected(searchInput)
        setSearchInput('')
      }
    }

    const keyDownHandler = (evt: React.KeyboardEvent<HTMLElement>) => {
      if (evt.key === 'Enter') {
        evt.preventDefault()
        handleSearchInputSelected()
      } else if (evt.key === 'Escape') {
        evt.preventDefault()
        setSearchInput('')
      }
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const searchString = e.currentTarget.value
      setSearchInput(searchString)
      if (!onSearchInputChange) {
        setFilteredOptions(options.filter((option) => !!option.label && option.label.toLowerCase().includes(searchString.toLowerCase())))
      } else {
        setLoadingSearchInput(true)
      }
    }

    const handleOnClick = (e: React.MouseEvent<HTMLInputElement>) => {
      if (isOpen) {
        e.stopPropagation()
      }
    }

    const handleClearOnClick = (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation()
      setSearchInput('')
    }

    useEffect(() => {
      setFilteredOptions(options)
    }, [options, setFilteredOptions])

    const getMainDisplayElement = () => {
      const Icon = () =>
        IconComponent ? <IconComponent selectedOption={selectedOption} /> : <>{icon ? <i className={`bi ${icon} ${iconClassName}`} /> : null}</>
      if (iconOnly) {
        return <Icon />
      }

      if (label) {
        return (
          <>
            <Icon />
            <div className='button-small text-truncate'>{label}</div>
          </>
        )
      }

      if (searchable) {
        return (
          <>
            <Icon />
            <input
              ref={searchInputRef}
              className={classNames(`form-control border-0 p-0 ${searchInputClassName}`, {
                invalid: isValidUserInput(),
                [styles.selectDropdownInputDisabled]: disabled,
              })}
              type='text'
              value={searchInput}
              onChange={(e) => handleInputChange(e)}
              onClick={(e) => handleOnClick(e)}
              onKeyDown={keyDownHandler}
              placeholder={placeholder}
              disabled={disabled}
              {...(inputAutoComplete ? { autoComplete: inputAutoComplete } : {})}
              {...(inputName ? { name: inputName } : {})}
            />
            {isOpen && searchInput && !hideClearButton ? <i className='bi bi-x' onClick={(e) => handleClearOnClick(e)} /> : null}
          </>
        )
      }

      if (selectedOption) {
        return (
          <div className='select-dropdown__selected-option-container d-flex gap-2 align-items-center w-100 overflow-hidden'>
            <Icon />
            <div className={classNames(` ${styles.selectContainerSelectedOption} d-flex gap-1 flex-wrap`)}>
              <div className='text-truncate'>{selectedOption.label}</div>
            </div>
          </div>
        )
      }

      return (
        <>
          <Icon />
          <div className='paragraph-medium zocks-text-gray-500 w-100 select-dropdown__placeholder'>{placeholder}</div>
        </>
      )
    }
    const handleButtonKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (searchable && e.key === ' ') {
        e.preventDefault()
        setSearchInput(searchInput + ' ')
      }
    }

    return (
      <Container ref={containerRef} className='mw-100'>
        <Row>
          <Col className='p-0 w-100 position-relative'>
            <Row>
              <Col className='d-flex gap-1'>
                <Button
                  type='button'
                  className={classNames(`${styles.selectContainer} ${className ? className : ''}`, {
                    [styles.selectContainerOpen]: isOpen,
                    [styles.selectContainerDisabled]: disabled,
                  })}
                  id='selectDropdown'
                  onClick={toggleDropdown}
                  onKeyDown={(e) => handleButtonKeyDown(e)}
                  loading={loading}
                >
                  {getMainDisplayElement()}
                  {hideArrows ? null : isOpen ? <i className='bi bi-chevron-contract' /> : <i className='bi bi-chevron-expand' />}
                </Button>
                {showSearchInputSelectButton ? (
                  <Button variant='primary' className='add-user-btn' onClick={handleSearchInputSelected} disabled={!isValidUserInput()}>
                    Add
                  </Button>
                ) : null}
              </Col>
            </Row>

            <div
              ref={dropdownDivRef}
              className={`dropdown-menu ${isOpen ? 'show' : ''} ${styles.selectDropdownMenu} paragraph-small`}
              aria-labelledby='selectDropdown'
            >
              {DropdownHeaderComponent ? <DropdownHeaderComponent /> : null}
              <div className={`${styles.selectDropdownOptionsContainer}`}>
                {loading || loadingSearchInput ? (
                  <div className='d-flex align-items-center justify-content-center'>
                    <Spinner size='sm' />
                  </div>
                ) : (
                  <>
                    {selectedOption && showDeselectOption && (
                      <DeselectDropDownItemOption optionId={'deselect-option'} optionLabel={deselectOptionLabel} onClick={handleOnDeselect} deselect={true} />
                    )}
                    {filteredOptions.length ? (
                      filteredOptions.map((option) => (
                        <DropDownItemOption
                          key={option.id}
                          optionId={option.id}
                          optionLabel={option.label}
                          displayLabel={option.displayLabel ?? option.label}
                          tooltipLabel={option.tooltipLabel}
                          selected={selectedOption?.id === option.id}
                          disabled={option.disabled}
                          onClick={() => handleOnOptionSelected(option)}
                          className={option.className}
                        />
                      ))
                    ) : (
                      <DefaultDropdownItemOption
                        key='No result'
                        optionId='No result'
                        optionLabel='No options'
                        onClick={() => {}}
                        disabled={true}
                        selected={false}
                      />
                    )}
                  </>
                )}
              </div>
              {DropdownFooterComponent ? <DropdownFooterComponent /> : null}
            </div>
          </Col>
        </Row>
      </Container>
    )
  },
)
